/**
 * WordPress dependencies
 */
import { _x } from '@safe-wordpress/i18n';

/**
 * Internal dependencies
 */
import type {
	OverflowControlSelectControlProps,
	OverflowControlProps,
	OverflowControlValue,
} from './types';

export const LABELS = {
	all: _x( 'All sides', 'text', 'nelio-ab-testing' ),
	vertical: _x( 'Top and bottom sides', 'text', 'nelio-ab-testing' ),
	horizontal: _x( 'Left and right sides', 'text', 'nelio-ab-testing' ),
};

export const DEFAULT_VALUES = {
	horizontal: undefined,
	vertical: undefined,
};

export const ALL_SIDES = [ 'horizontal', 'vertical' ] as const;

/**
 * Gets an items with the most occurrence within an array
 * https://stackoverflow.com/a/20762713
 *
 * @param arr Array of items to check.
 * @return The item with the most occurrences.
 */
function mode< T >( arr: T[] ) {
	return arr
		.sort(
			( a, b ) =>
				arr.filter( ( v ) => v === a ).length -
				arr.filter( ( v ) => v === b ).length
		)
		.pop();
}

/**
 * Gets the merged input value and unit from values data.
 *
 * @param values         Box values.
 * @param availableSides Available box sides to evaluate.
 *
 * @return A value + unit for the 'all' input.
 */
export function getMergedValue(
	values: OverflowControlValue = {},
	availableSides: OverflowControlProps[ 'sides' ] = ALL_SIDES
): string | undefined {
	const sides = normalizeSides( availableSides );
	if (
		sides.every(
			( side: keyof OverflowControlValue ) =>
				values[ side ] ===
				values[ sides[ 0 ] as keyof OverflowControlValue ]
		)
	) {
		return values[ sides[ 0 ] as keyof OverflowControlValue ];
	}

	return undefined;
}

/**
 * Checks if the values are mixed.
 *
 * @param values         Box values.
 * @param availableSides Available box sides to evaluate.
 * @return Whether the values are mixed.
 */
export function isValueMixed(
	values: OverflowControlValue = {},
	availableSides: OverflowControlProps[ 'sides' ] = ALL_SIDES
): boolean {
	return availableSides.some(
		( side: keyof OverflowControlValue ) =>
			values[ side ] !==
			values[ availableSides[ 0 ] as keyof OverflowControlValue ]
	);
}

/**
 * Determine the most common unit selection to use as a fallback option.
 *
 * @param selectedUnits Current unit selections for individual sides.
 * @return  Most common unit selection.
 */
export function getAllUnitFallback(
	selectedUnits?: OverflowControlValue
): string | undefined {
	if ( ! selectedUnits || typeof selectedUnits !== 'object' ) {
		return undefined;
	}

	const filteredUnits = Object.values( selectedUnits ).filter( Boolean );

	return mode( filteredUnits );
}

/**
 * Checks to determine if values are defined.
 *
 * @param values Box values.
 *
 * @return  Whether values are mixed.
 */
export function isValuesDefined( values?: OverflowControlValue ): boolean {
	return (
		!! values &&
		Object.values( values ).filter( ( value ) => !! value ).length > 0
	);
}

/**
 * Get initial selected side, factoring in whether the sides are linked,
 * and whether the vertical / horizontal directions are grouped via splitOnAxis.
 *
 * @param isLinked Whether the box control's fields are linked.
 * @return The initial side.
 */
export function getInitialSide( isLinked: boolean ): keyof typeof LABELS {
	let initialSide: keyof typeof LABELS = 'all';

	if ( ! isLinked ) {
		initialSide = 'horizontal';
	}

	return initialSide;
}

/**
 * Normalizes provided sides configuration to an array containing only top,
 * right, bottom and left. This essentially just maps `horizontal` or `vertical`
 * to their appropriate sides to facilitate correctly determining value for
 * all input control.
 *
 * @param sides Available sides for box control.
 * @return Normalized sides configuration.
 */
export function normalizeSides(
	sides: OverflowControlProps[ 'sides' ]
): ( keyof OverflowControlValue )[] {
	const filteredSides: ( keyof OverflowControlValue )[] = [];

	if ( ! sides?.length ) {
		return [ ...ALL_SIDES ];
	}

	if ( sides.includes( 'vertical' ) ) {
		filteredSides.push( ...( [ 'vertical' ] as const ) );
	} else if ( sides.includes( 'horizontal' ) ) {
		filteredSides.push( ...( [ 'horizontal' ] as const ) );
	} else {
		const newSides = ALL_SIDES.filter( ( side ) => sides.includes( side ) );
		filteredSides.push( ...newSides );
	}

	return filteredSides;
}

/**
 * Applies a value to an object representing top, right, bottom and left sides
 * while taking into account any custom side configuration.
 *
 * @deprecated
 *
 * @param currentValues The current values for each side.
 * @param newValue      The value to apply to the sides object.
 * @param sides         Array defining valid sides.
 *
 * @return Object containing the updated values for each side.
 */
export function applyValueToSides(
	currentValues: OverflowControlValue,
	newValue?: string,
	sides?: OverflowControlProps[ 'sides' ]
): OverflowControlValue {
	const newValues = { ...currentValues };

	if ( sides?.length ) {
		sides.forEach( ( side ) => {
			if ( side === 'vertical' ) {
				newValues.vertical = newValue;
			} else if ( side === 'horizontal' ) {
				newValues.horizontal = newValue;
			}
		} );
	} else {
		ALL_SIDES.forEach( ( side ) => ( newValues[ side ] = newValue ) );
	}

	return newValues;
}

/**
 * Return the allowed sides based on the sides configuration.
 *
 * @param sides Sides configuration.
 * @return Allowed sides.
 */
export function getAllowedSides(
	sides: OverflowControlSelectControlProps[ 'sides' ]
): Set< keyof OverflowControlValue > {
	const allowedSides: Set< keyof OverflowControlValue > = new Set(
		! sides ? ALL_SIDES : []
	);
	sides?.forEach( ( allowedSide ) => {
		if ( allowedSide === 'vertical' ) {
			allowedSides.add( 'vertical' );
		} else if ( allowedSide === 'horizontal' ) {
			allowedSides.add( 'horizontal' );
		} else {
			allowedSides.add( allowedSide );
		}
	} );
	return allowedSides;
}
